import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Modal,
  TextInput,
  ActivityIndicator,
  Alert,
  Dimensions,
  Share,
  Linking,
  Animated,
  FlatList,
  RefreshControl,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons, FontAwesome5 } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';

import axios from 'axios';

const { width, height } = Dimensions.get('window');
const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token'); if (t) c.headers.Authorization = 'Bearer ' + t; const bid = await AsyncStorage.getItem('company_id'); if (bid) c.params = { ...c.params, company_id: parseInt(bid) }; } catch {} return c; }, e => Promise.reject(e));
const EP = { applications: API_BASE_URL + '/api/data/applications', jobs: API_BASE_URL + '/api/data/jobs', companyProfile: API_BASE_URL + '/api/data/company_profiles' };

export default function PostJobDetailsScreen({ navigation, route }) {
  const { job } = route?.params || {};
  const [jobData, setJobData] = useState(job || {});
  const [companyLogo, setCompanyLogo] = useState(route.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route.params?.companyName || '');
  const [companyProfile, setCompanyProfile] = useState(route.params?.companyProfile || null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showActionModal, setShowActionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [notes, setNotes] = useState('');
  const [showNotesModal, setShowNotesModal] = useState(false);

  const slideAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(1)).current;

  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'information-outline' },
    { id: 'applicants', name: 'Applicants', icon: 'account-group' },
    { id: 'analytics', name: 'Analytics', icon: 'chart-line' },
    { id: 'social', name: 'Social', icon: 'share-variant' },
    { id: 'testing', name: 'Testing', icon: 'clipboard-check-outline' },
    { id: 'settings', name: 'Settings', icon: 'cog-outline' },
  ];

  const jobActions = [
    {
      id: 'edit',
      title: 'Edit Job',
      subtitle: 'Modify job details',
      icon: 'pencil',
      color: '#3B82F6',
      action: () => handleEditJob(),
    },
    {
      id: 'duplicate',
      title: 'Duplicate Job',
      subtitle: 'Create a copy',
      icon: 'content-copy',
      color: '#10B981',
      action: () => handleDuplicateJob(),
    },
    {
      id: 'share',
      title: 'Share Job',
      subtitle: 'Share job posting',
      icon: 'share-variant',
      color: '#8B5CF6',
      action: () => setShowShareModal(true),
    },
    {
      id: 'analytics',
      title: 'View Analytics',
      subtitle: 'Performance insights',
      icon: 'chart-box',
      color: '#F59E0B',
      action: () => navigation.navigate('JobAnalyticsScreen', { jobId: jobData.id }),
    },
    {
      id: 'pause',
      title: (jobData.status || 'draft') === 'active' ? 'Pause Job' : 'Activate Job',
      subtitle: (jobData.status || 'draft') === 'active' ? 'Temporarily pause' : 'Make active',
      icon: (jobData.status || 'draft') === 'active' ? 'pause' : 'play',
      color: (jobData.status || 'draft') === 'active' ? '#F59E0B' : '#10B981',
      action: () => handleToggleStatus(),
    },
    {
      id: 'close',
      title: 'Close Job',
      subtitle: 'Mark as closed',
      icon: 'close-circle',
      color: '#EF4444',
      action: () => handleCloseJob(),
    },
  ];

  const shareOptions = [
    { id: 'link', title: 'Copy Link', icon: 'link', color: '#3B82F6', action: () => handleCopyLink() },
    { id: 'email', title: 'Share via Email', icon: 'email', color: '#10B981', action: () => handleShareEmail() },
    { id: 'linkedin', title: 'Post on LinkedIn', icon: 'linkedin', color: '#0077B5', action: () => handleShareLinkedIn() },
    { id: 'whatsapp', title: 'Share on WhatsApp', icon: 'whatsapp', color: '#25D366', action: () => handleShareWhatsApp() },
    { id: 'twitter', title: 'Post on X (Twitter)', icon: 'twitter', color: '#1DA1F2', action: () => handleShareTwitter() },
    { id: 'facebook', title: 'Post on Facebook', icon: 'facebook', color: '#1877F2', action: () => handleShareFacebook() },
    { id: 'telegram', title: 'Share on Telegram', icon: 'send-circle', color: '#0088CC', action: () => handleShareTelegram() },
    { id: 'indeed', title: 'Post on Indeed', icon: 'briefcase-search', color: '#2164F3', action: () => handleShareIndeed() },
    { id: 'glassdoor', title: 'Post on Glassdoor', icon: 'star-circle', color: '#0CAA41', action: () => handleShareGlassdoor() },
    { id: 'sms', title: 'Share via SMS', icon: 'message-text', color: '#6B7280', action: () => handleShareSMS() },
    { id: 'qrcode', title: 'Generate QR Code', icon: 'qrcode', color: '#1F2937', action: () => handleGenerateQR() },
    { id: 'embed', title: 'Get Embed Code', icon: 'code-tags', color: '#8B5CF6', action: () => handleGetEmbed() },
    { id: 'pdf', title: 'Download PDF', icon: 'file-pdf-box', color: '#EF4444', action: () => generateJobPDF() },
    { id: 'card', title: 'Social Media Card', icon: 'card-account-details', color: '#0e4e96', action: () => generateJobCard() },
  ];

  useEffect(() => {
    loadJobDetails();
    loadApplicants();
    loadAnalytics();
    loadNotes();
    loadCompanyProfile();
  }, []);
  const loadCompanyProfile = async () => {
    try {
      // Try all possible AsyncStorage keys for company profile
      const cached = await AsyncStorage.getItem('companyProfile') 
        || await AsyncStorage.getItem('company_profile')
        || await AsyncStorage.getItem('business_company_data')
        || await AsyncStorage.getItem('selected_company');
      if (cached) {
        const cp = JSON.parse(cached);
        console.log('📋 Company profile keys:', Object.keys(cp).filter(k => k.toLowerCase().includes('logo') || k.toLowerCase().includes('image') || k.toLowerCase().includes('avatar') || k.toLowerCase().includes('banner') || k.toLowerCase().includes('photo')));
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || cp.image_url || cp.profile_image || cp.avatar || cp.company_image || cp.logo_image || cp.brand_logo || cp.photo || cp.picture || cp.icon || cp.thumbnail || null;
        console.log('🖼️ Logo found from cache:', logo ? 'YES' : 'NO', logo?.substring(0, 60));
        if (logo) setCompanyLogo(logo);
        setCompanyProfile(cp);
      }
      // Try all possible company ID keys
      const cid = await AsyncStorage.getItem('company_id')
        || await AsyncStorage.getItem('company_id')
        || await AsyncStorage.getItem('companyId')
        || job?.company_id
        || '1';
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') ||
                    await AsyncStorage.getItem('token');
      const res = await fetch(
        `https://api.jobeng.ai/api/data/company_profiles?company_id=${cid}`,
        { headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      if (!res.ok) return;
      const r = await res.json();
      const cp = Array.isArray(r) ? r[0] : (r.data?.[0] ?? r.data ?? r);
      if (cp && typeof cp === 'object') {
        console.log('📋 API profile keys:', Object.keys(cp).filter(k => k.toLowerCase().includes('logo') || k.toLowerCase().includes('image') || k.toLowerCase().includes('avatar') || k.toLowerCase().includes('banner') || k.toLowerCase().includes('photo')));
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const logo = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || cp.image_url || cp.profile_image || cp.avatar || cp.company_image || cp.logo_image || cp.brand_logo || cp.photo || cp.picture || cp.icon || cp.thumbnail || null;
        console.log('🖼️ Logo found from API:', logo ? 'YES' : 'NO', logo?.substring(0, 60));
        if (logo) setCompanyLogo(logo);
        setCompanyProfile(cp);
        await AsyncStorage.setItem('company_profile', JSON.stringify(cp));
        await AsyncStorage.setItem('companyProfile', JSON.stringify(cp));
      }
    } catch (e) { console.warn('LoadCompanyProfile error:', e.message); }
  };


  const loadJobDetails = async () => {
    try {
      const savedJobs = await AsyncStorage.getItem('jobs');
      if (savedJobs) {
        const jobsData = JSON.parse(savedJobs);
        const updatedJob = jobsData.find(j => j.id === jobData.id);
        if (updatedJob) {
          setJobData(updatedJob);
        }
      }
    } catch (error) {
      console.error('Error loading job details:', error);
    }
  };

  const loadApplicants = async () => {
    try {
      const cid = await AsyncStorage.getItem('company_id') || '1';
      const params = { company_id: parseInt(cid), limit: 100 };
      if (jobData?.id) { params.job_id = jobData.id; params.job_id = jobData.id; }
      const r = await api.get(EP.applications, { params }).catch(() => null);
      const apiApps = r?.data?.success && Array.isArray(r.data.data) ? r.data.data : [];
      if (apiApps.length > 0) {
        // Client-side filter by job if API didn't
        let filtered = apiApps;
        if (jobData?.id) {
          const jid = String(jobData.id);
          const byId = apiApps.filter(a => String(a.job_id || a.job_id) === jid);
          if (byId.length > 0) filtered = byId;
          else if (jobData.title) {
            const byTitle = apiApps.filter(a => (a.job_title || '').toLowerCase() === String(jobData.title ?? '').toLowerCase());
            if (byTitle.length > 0) filtered = byTitle;
          }
        }
        const mapped = filtered.map((a, i) => ({
          id: a.id || `app_${i}`, name: a.candidate_name || a.applicant_name || a.name || 'Unknown',
          email: a.candidate_email || a.email || '', phone: a.candidate_phone || a.phone || '',
          avatar: null, role: a.current_title || a.position || jobData.title || '',
          experience: a.experience || '3+ years', location: a.candidate_location || a.location || '',
          stage: a.status || a.stage || 'new', score: a.ai_score || Math.floor(Math.random() * 30) + 70,
          appliedDate: a.applied_date || a.created_at || new Date().toISOString(),
          currentCompany: a.current_company || '',
        }));
        setApplicants(mapped);
      } else {
        const mockApplicants = generateMockApplicants();
        setApplicants(mockApplicants);
      }
    } catch (error) {
      console.error('Error loading applicants:', error);
      const mockApplicants = generateMockApplicants();
      setApplicants(mockApplicants);
    }
  };

  const loadAnalytics = async () => {
    try {
      // Generate mock analytics for demonstration
      const mockAnalytics = {
        views: jobData.views || Math.floor(Math.random() * 200) + 50,
        applications: jobData.applicants || Math.floor(Math.random() * 30) + 5,
        conversionRate: Math.floor(Math.random() * 15) + 5,
        averageTimeToApply: Math.floor(Math.random() * 5) + 2,
        topSources: [
          { source: 'Direct', count: 15, percentage: 45 },
          { source: 'LinkedIn', count: 10, percentage: 30 },
          { source: 'Indeed', count: 5, percentage: 15 },
          { source: 'Company Website', count: 3, percentage: 10 },
        ],
        dailyViews: Array.from({ length: 7 }, (_, i) => ({
          day: new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000).toLocaleDateString('en-GB', { weekday: 'short' }),
          views: Math.floor(Math.random() * 20) + 5,
        })),
      };
      setAnalytics(mockAnalytics);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadNotes = async () => {
    try {
      const savedNotes = await AsyncStorage.getItem(`job_notes_${jobData.id}`);
      if (savedNotes) {
        setNotes(savedNotes);
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const saveNotes = async () => {
    try {
      await AsyncStorage.setItem(`job_notes_${jobData.id}`, notes);
      setShowNotesModal(false);
      Alert.alert('Success', 'Notes saved successfully!');
    } catch (error) {
      console.error('Error saving notes:', error);
      Alert.alert('Error', 'Failed to save notes. Please try again.');
    }
  };

  const generateMockApplicants = () => {
    const names = [
      'John Smith', 'Sarah Johnson', 'Michael Brown', 'Emma Wilson',
      'James Davis', 'Lisa Anderson', 'David Miller', 'Jennifer Taylor',
      'Robert Garcia', 'Maria Rodriguez', 'William Martinez', 'Jessica Lee'
    ];
    
    const statuses = ['new', 'reviewed', 'shortlisted', 'interviewed', 'rejected', 'hired'];
    const sources = ['LinkedIn', 'Indeed', 'Direct', 'Company Website', 'Referral'];

    return Array.from({ length: Math.min(jobData.applicants || 8, 12) }, (_, index) => ({
      id: `applicant_${index + 1}`,
      name: names[index % names.length],
      email: `${names[index % names.length].toLowerCase().replace(' ', '.')}@email.com`,
      phone: `+44 ${Math.floor(Math.random() * 9000000000) + 1000000000}`,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      source: sources[Math.floor(Math.random() * sources.length)],
      appliedAt: new Date(Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000).toISOString(),
      experience: `${Math.floor(Math.random() * 10) + 1} years`,
      rating: Math.floor(Math.random() * 5) + 1,
      skills: ['JavaScript', 'React', 'Node.js', 'Python', 'AWS'].slice(0, Math.floor(Math.random() * 3) + 2),
    }));
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadJobDetails();
    await loadApplicants();
    await loadAnalytics();
    setRefreshing(false);
  };

  const handleEditJob = () => {
    setShowActionModal(false);
    navigation.navigate('EditJobScreen', { jobId: jobData.id, job: jobData, companyLogo, companyName, companyProfile: companyProfile || {} });
  };

  const handleDuplicateJob = () => {
    setShowActionModal(false);
    navigation.navigate('CreateJobScreen', { job: jobData, mode: 'duplicate', companyLogo, companyName });
  };

  const handleToggleStatus = async () => {
    const newStatus = (jobData.status || 'draft') === 'active' ? 'paused' : 'active';
    try {
      const savedJobs = await AsyncStorage.getItem('jobs');
      if (savedJobs) {
        const jobsData = JSON.parse(savedJobs);
        const updatedJobs = jobsData.map(j => 
          j.id === jobData.id ? { ...j, status: newStatus, updatedAt: new Date().toISOString() } : j
        );
        await AsyncStorage.setItem('jobs', JSON.stringify(updatedJobs));
        // Sync to API — shared helper flips both is_active AND status so the
        // job immediately appears or disappears from professional search
        try {
          const { setJobActive } = require('./jobsShared');
          await setJobActive(jobData.id, newStatus === 'active');
        } catch (e) { console.warn('API sync failed:', e.message); }
        setJobData({ ...jobData, status: newStatus });
        setShowActionModal(false);
        Alert.alert('Success', `Job ${newStatus} successfully!`);
      }
    } catch (error) {
      console.error('Error updating job status:', error);
      Alert.alert('Error', 'Failed to update job status. Please try again.');
    }
  };

  const handleCloseJob = () => {
    Alert.alert(
      'Close Job',
      'Are you sure you want to close this job? This will stop accepting new applications.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Close Job',
          style: 'destructive',
          onPress: async () => {
            try {
              const savedJobs = await AsyncStorage.getItem('jobs');
              if (savedJobs) {
                const jobsData = JSON.parse(savedJobs);
                const updatedJobs = jobsData.map(j => 
                  j.id === jobData.id ? { ...j, status: 'closed', updatedAt: new Date().toISOString() } : j
                );
                await AsyncStorage.setItem('jobs', JSON.stringify(updatedJobs));
                // Sync closure to server so professional search drops this job
                try {
                  const { setJobActive } = require('./jobsShared');
                  await setJobActive(jobData.id, false);
                } catch (e) { console.warn('API close sync failed:', e.message); }
                setJobData({ ...jobData, status: 'closed' });
                setShowActionModal(false);
                Alert.alert('Success', 'Job closed successfully!');
              }
            } catch (error) {
              console.error('Error closing job:', error);
              Alert.alert('Error', 'Failed to close job. Please try again.');
            }
          }
        }
      ]
    );
  };

  const handleCopyLink = async () => {
    const jobLink = `https://jobeng.app/jobs/${jobData.id}`;
    try {
      await Share.share({
        message: jobLink,
        title: `Job: ${jobData.title}`,
      });
      setShowShareModal(false);
    } catch (error) {
      console.error('Error sharing link:', error);
    }
  };

  const handleShareEmail = async () => {
    const subject = `Job Opportunity: ${jobData.title}`;
    const body = `Check out this job opportunity:\n\n${jobData.title}\n${jobData.department}\n${jobData.location}\n\nSalary: ${formatCurrency(jobData.salaryMin)} - ${formatCurrency(jobData.salaryMax)}\n\nApply now: https://jobeng.app/jobs/${jobData.id}`;
    
    try {
      await Linking.openURL(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
      setShowShareModal(false);
    } catch (error) {
      console.error('Error opening email:', error);
    }
  };

  // Share with rich content including logo
  const getShareMessage = () => {
    const lines = [
      companyLogo ? `[Company Logo: ${companyLogo}]` : '',
      `🚀 We're Hiring: ${jobData.title}`,
      `🏢 ${companyName || 'Company'}`,
      `📍 ${jobData.location || 'Remote'}`,
      `💼 ${jobData.department || ''}`,
      `💰 ${formatCurrency(jobData.salaryMin)} - ${formatCurrency(jobData.salaryMax)}`,
      `⏰ ${jobData.type || 'Full-time'}`,
      '',
      `✅ Apply: https://jobeng.app/jobs/${jobData.id}`,
    ].filter(Boolean);
    return lines.join('\n');
  };

  const handleShareLinkedIn = async () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=https://jobeng.app/jobs/${jobData.id}`;
    try {
      await Linking.openURL(url);
      setShowShareModal(false);
    } catch (error) {
      console.error('Error opening LinkedIn:', error);
    }
  };

  const handleShareWhatsApp = async () => {
    const message = `Check out this job opportunity: ${jobData.title} at ${jobData.department}. Apply now: https://jobeng.app/jobs/${jobData.id}`;
    const url = `whatsapp://send?text=${encodeURIComponent(message)}`;
    try {
      await Linking.openURL(url);
      setShowShareModal(false);
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
    }
  };

  const handleShareTwitter = async () => {
    const text = `We're hiring! ${jobData.title} at ${companyName}. Apply now:`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(`https://jobeng.app/jobs/${jobData.id}`)}`;
    try { await Linking.openURL(url); setShowShareModal(false); } catch (e) { console.error('Error opening Twitter:', e); }
  };

  const handleShareFacebook = async () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://jobeng.app/jobs/${jobData.id}`)}&quote=${encodeURIComponent(`We're hiring: ${jobData.title}`)}`;
    try { await Linking.openURL(url); setShowShareModal(false); } catch (e) { console.error('Error opening Facebook:', e); }
  };

  const handleShareTelegram = async () => {
    const text = `🚀 Job Opportunity: ${jobData.title} at ${companyName}\n📍 ${jobData.location}\n💰 ${formatCurrency(jobData.salaryMin)} - ${formatCurrency(jobData.salaryMax)}\n\nApply: https://jobeng.app/jobs/${jobData.id}`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(`https://jobeng.app/jobs/${jobData.id}`)}&text=${encodeURIComponent(text)}`;
    try { await Linking.openURL(url); setShowShareModal(false); } catch (e) { console.error('Error opening Telegram:', e); }
  };

  const handleShareIndeed = async () => {
    const url = `https://employers.indeed.com/post-a-job?title=${encodeURIComponent(jobData.title)}&location=${encodeURIComponent(jobData.location || '')}`;
    try { await Linking.openURL(url); setShowShareModal(false); } catch (e) { Alert.alert('Indeed', 'Visit employers.indeed.com to post this job on Indeed'); }
  };

  const handleShareGlassdoor = async () => {
    const url = `https://www.glassdoor.com/employer/post-job`;
    try { await Linking.openURL(url); setShowShareModal(false); } catch (e) { Alert.alert('Glassdoor', 'Visit glassdoor.com to post this job'); }
  };

  const handleShareSMS = async () => {
    const body = `Job Opportunity: ${jobData.title} at ${companyName}. Apply: https://jobeng.app/jobs/${jobData.id}`;
    try { await Linking.openURL(`sms:?body=${encodeURIComponent(body)}`); setShowShareModal(false); } catch (e) { console.error('Error opening SMS:', e); }
  };

  const handleGenerateQR = async () => {
    const jobUrl = `https://jobeng.app/jobs/${jobData.id}`;
    Alert.alert('QR Code', `Job URL for QR generation:\n\n${jobUrl}\n\nUse any QR generator with this URL.`, [
      { text: 'Copy URL', onPress: async () => { try { await Share.share({ message: jobUrl }); } catch {} } },
      { text: 'OK' }
    ]);
    setShowShareModal(false);
  };

  const handleGetEmbed = async () => {
    const embedCode = `<iframe src="https://jobeng.app/embed/jobs/${jobData.id}" width="100%" height="600" frameborder="0"></iframe>`;
    try {
      await Share.share({ message: embedCode, title: 'Embed Code' });
      setShowShareModal(false);
    } catch (e) { console.error('Error sharing embed:', e); }
  };


  // Generate professional PDF job posting with company logo
  const generateJobPDF = async () => {
    try {
      const logoImg = companyLogo 
        ? '<img src="' + companyLogo + '" width="80" height="80" style="border-radius:12px;border:2px solid #E5E7EB;" />'
        : '<div style="display:inline-block;width:80px;height:80px;border-radius:12px;background:#0e4e96;text-align:center;line-height:80px;color:#fff;font-size:28px;font-weight:900;">' + (companyName || 'CO').split(' ').slice(0,2).map(function(w){return w[0]}).join('').toUpperCase() + '</div>';

      var salaryText = formatCurrency(jobData.salaryMin) + ' - ' + formatCurrency(jobData.salaryMax);
      var jobTitle = jobData.title || 'Job Position';
      var company = companyName || 'Company';
      var dept = jobData.department || '';
      var loc = jobData.location || 'Remote';
      var jtype = jobData.type || 'Full-time';
      var desc = (jobData.description || 'No description provided.').replace(/\n/g, '<br>');
      var reqs = jobData.requirements ? jobData.requirements.replace(/\n/g, '<br>') : '';
      var bens = jobData.benefits ? jobData.benefits.replace(/\n/g, '<br>') : '';
      var exp = jobData.experience || 'Not specified';
      var posted = jobData.postedDate ? new Date(jobData.postedDate).toLocaleDateString('en-GB') : 'Recently';
      var today = new Date().toLocaleDateString('en-GB');
      var year = new Date().getFullYear();

      var html = '<!DOCTYPE html><html><head><meta charset="utf-8"><style>' +
        '@page { margin: 30px; }' +
        'body { font-family: Helvetica, Arial, sans-serif; margin: 0; padding: 20px; color: #1F2937; font-size: 14px; }' +
        '.header-table { width: 100%; border-bottom: 3px solid #0e4e96; padding-bottom: 16px; margin-bottom: 24px; }' +
        '.header-table td { vertical-align: middle; }' +
        'h1.company { margin: 0; font-size: 22px; color: #0e4e96; font-weight: 800; }' +
        '.company-sub { margin: 4px 0 0; color: #6B7280; font-size: 13px; }' +
        'h2.job-title { font-size: 26px; font-weight: 900; color: #1F2937; margin: 0 0 12px 0; }' +
        '.badge { display: inline-block; padding: 5px 14px; border-radius: 14px; font-size: 12px; font-weight: 700; margin-right: 6px; margin-bottom: 6px; }' +
        '.b-dept { background: #DBEAFE; color: #1E40AF; }' +
        '.b-loc { background: #D1FAE5; color: #065F46; }' +
        '.b-sal { background: #FEF3C7; color: #92400E; }' +
        '.b-type { background: #F3E8FF; color: #6B21A8; }' +
        '.section { margin-top: 22px; }' +
        '.section h3 { font-size: 16px; color: #0e4e96; border-bottom: 1px solid #E5E7EB; padding-bottom: 6px; margin: 0 0 10px 0; }' +
        '.section p { font-size: 13px; line-height: 1.7; color: #374151; margin: 0; }' +
        '.details-table { width: 100%; border-collapse: separate; border-spacing: 6px; margin-top: 10px; }' +
        '.details-table td { background: #F9FAFB; padding: 10px 12px; border-radius: 6px; width: 50%; vertical-align: top; }' +
        '.d-label { font-size: 10px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.5px; margin: 0; }' +
        '.d-value { font-size: 14px; font-weight: 700; color: #1F2937; margin: 3px 0 0; }' +
        '.apply-box { margin-top: 28px; padding: 18px; background: #EFF6FF; border-radius: 10px; border: 2px solid #0e4e96; text-align: center; }' +
        '.apply-box h4 { color: #0e4e96; margin: 0 0 6px; font-size: 16px; }' +
        '.apply-box p { color: #6B7280; margin: 0; font-size: 12px; }' +
        '.apply-link { color: #0e4e96; font-weight: 800; font-size: 15px; text-decoration: none; }' +
        '.footer { margin-top: 30px; padding-top: 12px; border-top: 1px solid #E5E7EB; text-align: center; color: #9CA3AF; font-size: 10px; }' +
        '</style></head><body>' +
        '<table class="header-table" cellpadding="0" cellspacing="0"><tr>' +
        '<td style="width:96px;">' + logoImg + '</td>' +
        '<td style="padding-left:16px;"><h1 class="company">' + company + '</h1>' +
        '<p class="company-sub">' + dept + (loc ? ' &middot; ' + loc : '') + '</p></td></tr></table>' +
        '<h2 class="job-title">' + jobTitle + '</h2>' +
        '<div style="margin-bottom:18px;">' +
        '<span class="badge b-dept">' + (dept || 'General') + '</span>' +
        '<span class="badge b-loc">' + loc + '</span>' +
        '<span class="badge b-sal">' + salaryText + '</span>' +
        '<span class="badge b-type">' + jtype + '</span></div>' +
        '<div class="section"><h3>Job Description</h3><p>' + desc + '</p></div>' +
        (reqs ? '<div class="section"><h3>Requirements</h3><p>' + reqs + '</p></div>' : '') +
        (bens ? '<div class="section"><h3>Benefits</h3><p>' + bens + '</p></div>' : '') +
        '<div class="section"><h3>Job Details</h3>' +
        '<table class="details-table"><tr>' +
        '<td><p class="d-label">Department</p><p class="d-value">' + (dept || '-') + '</p></td>' +
        '<td><p class="d-label">Location</p><p class="d-value">' + loc + '</p></td></tr><tr>' +
        '<td><p class="d-label">Employment Type</p><p class="d-value">' + jtype + '</p></td>' +
        '<td><p class="d-label">Experience</p><p class="d-value">' + exp + '</p></td></tr><tr>' +
        '<td><p class="d-label">Salary Range</p><p class="d-value">' + salaryText + '</p></td>' +
        '<td><p class="d-label">Posted</p><p class="d-value">' + posted + '</p></td></tr></table></div>' +
        '<div class="apply-box"><h4>Interested in this role?</h4><p>Apply online at</p>' +
        '<a class="apply-link" href="https://jobeng.app/jobs/' + jobData.id + '">jobeng.app/jobs/' + jobData.id + '</a></div>' +
        '<div class="footer"><p>Posted by ' + company + ' via JobEng &middot; ' + today + '</p>' +
        '<p>&copy; ' + year + ' JobEng - UK Employment Platform</p></div>' +
        '</body></html>';

      var result = await Print.printToFileAsync({ html: html });
      
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(result.uri, {
          mimeType: 'application/pdf',
          dialogTitle: jobTitle + ' - Job Posting',
          UTI: 'com.adobe.pdf',
        });
      } else {
        Alert.alert('PDF Generated', 'PDF saved but sharing is not available on this device.');
      }

      trackShare('pdf');
      setShowShareModal(false);
    } catch (error) {
      console.error('PDF generation error:', error);
      Alert.alert('Error', 'Failed to generate PDF. Please try again.');
    }
  };

    // Generate and share job post image card for social media
  const generateJobCard = async () => {
    try {
      const logoImg = companyLogo 
        ? '<img src="' + companyLogo + '" width="72" height="72" style="border-radius:14px;border:3px solid rgba(255,255,255,0.4);vertical-align:middle;" />'
        : '<div style="display:inline-block;width:72px;height:72px;border-radius:14px;background:rgba(255,255,255,0.2);border:3px solid rgba(255,255,255,0.4);text-align:center;line-height:72px;color:#fff;font-size:26px;font-weight:900;vertical-align:middle;">' + (companyName || 'CO').split(' ').slice(0,2).map(function(w){return w[0]}).join('').toUpperCase() + '</div>';

      const salaryText = formatCurrency(jobData.salaryMin) + ' - ' + formatCurrency(jobData.salaryMax);
      const jobTitle = jobData.title || 'Open Position';
      const jobType = jobData.type || 'Full-time';
      const jobDept = jobData.department || '';
      const jobLoc = jobData.location || 'Remote';
      const company = companyName || 'Company';

      const cardHtml = '<!DOCTYPE html><html><head><meta charset="utf-8"><style>' +
        '@page { margin: 0; }' +
        'body { margin: 0; padding: 0; font-family: Helvetica, Arial, sans-serif; background: #0e4e96; }' +
        '.page { width: 100%; min-height: 100vh; background: #0e4e96; background-image: linear-gradient(160deg, #0a3d78 0%, #0e4e96 30%, #1a6fce 70%, #2980d9 100%); padding: 60px; box-sizing: border-box; position: relative; }' +
        '.circle { position: absolute; border-radius: 50%; background: rgba(255,255,255,0.04); }' +
        '.c1 { width: 300px; height: 300px; top: -80px; right: -80px; }' +
        '.c2 { width: 200px; height: 200px; bottom: -40px; left: -40px; }' +
        '.logo-area { margin-bottom: 28px; }' +
        '.logo-name { display: inline-block; vertical-align: middle; margin-left: 16px; color: #fff; font-size: 22px; font-weight: 800; }' +
        '.badge { display: inline-block; background: #10B981; color: #fff; padding: 10px 24px; border-radius: 24px; font-size: 16px; font-weight: 800; letter-spacing: 1px; margin-bottom: 20px; }' +
        'h1 { color: #FFFFFF; font-size: 42px; font-weight: 900; margin: 0 0 14px 0; line-height: 1.15; }' +
        '.subtitle { color: #B8D4F0; font-size: 20px; margin: 0 0 28px 0; font-weight: 500; }' +
        '.tags { margin-bottom: 10px; }' +
        '.tag { display: inline-block; background: rgba(255,255,255,0.15); color: #FFFFFF; padding: 10px 18px; border-radius: 10px; font-size: 15px; font-weight: 700; margin-right: 10px; margin-bottom: 8px; }' +
        '.footer { position: absolute; bottom: 30px; left: 60px; right: 60px; }' +
        '.footer-left { color: #B8D4F0; font-size: 15px; font-weight: 700; }' +
        '.footer-right { float: right; color: rgba(255,255,255,0.4); font-size: 12px; }' +
        '</style></head><body>' +
        '<div class="page">' +
        '<div class="circle c1"></div><div class="circle c2"></div>' +
        '<div class="logo-area">' + logoImg + '<span class="logo-name">' + company + '</span></div>' +
        '<div class="badge">WE\'RE HIRING</div>' +
        '<h1>' + jobTitle + '</h1>' +
        '<p class="subtitle">' + company + ' &middot; ' + jobLoc + '</p>' +
        '<div class="tags">' +
        '<span class="tag">' + salaryText + '</span>' +
        '<span class="tag">' + jobType + '</span>' +
        (jobDept ? '<span class="tag">' + jobDept + '</span>' : '') +
        '</div>' +
        '<div class="footer">' +
        '<span class="footer-left">Apply: jobeng.app/jobs/' + jobData.id + '</span>' +
        '<span class="footer-right">Posted via JobEng</span>' +
        '</div>' +
        '</div></body></html>';

      const { uri } = await Print.printToFileAsync({ html: cardHtml });
      
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, {
          mimeType: 'application/pdf',
          dialogTitle: jobTitle + ' - Job Card',
        });
      }
      trackShare('social_card');
    } catch (error) {
      console.error('Card generation error:', error);
      Alert.alert('Error', 'Failed to generate social card.');
    }
  };

  // Share to all platforms at once
  // Get company logo URL for sharing
  const getLogoUrl = () => companyLogo || null;

  const handleShareToAll = async () => {
    const message = `🚀 We're Hiring: ${jobData.title}\n\n🏢 ${companyName}\n📍 ${jobData.location || 'Remote'}\n💼 ${jobData.department || ''}\n💰 ${formatCurrency(jobData.salaryMin)} - ${formatCurrency(jobData.salaryMax)}\n⏰ ${jobData.type || 'Full-time'}\n\n${(jobData.description || '').substring(0, 200)}...\n\n✅ Apply now: https://jobeng.app/jobs/${jobData.id}`;
    try {
      await Share.share({ message, title: `Job: ${jobData.title}` });
    } catch (e) { console.error('Error sharing:', e); }
  };

  // Track share analytics
  const trackShare = async (platform) => {
    try {
      const cid = await AsyncStorage.getItem('company_id') || '1';
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      await fetch(`https://api.jobeng.ai/api/data/job_share_analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ job_id: jobData.id, platform, company_id: parseInt(cid), shared_at: new Date().toISOString() }),
      });
    } catch (e) { console.warn('Share tracking failed:', e.message); }
  };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getDaysRemaining = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10B981';
      case 'draft': return '#F59E0B';
      case 'paused': return '#6B7280';
      case 'closed': return '#EF4444';
      default: return '#6B7280';
    }
  };

  const getApplicantStatusColor = (status) => {
    switch (status) {
      case 'new': return '#3B82F6';
      case 'reviewed': return '#8B5CF6';
      case 'shortlisted': return '#10B981';
      case 'interviewed': return '#F59E0B';
      case 'rejected': return '#EF4444';
      case 'hired': return '#059669';
      default: return '#6B7280';
    }
  };

  const renderHeader = () => (
    <LinearGradient
      colors={['#4A90E2', '#357ABD']}
      style={styles.header}
    >
      <SafeAreaView>
        <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.headerTitleContainer, { flexDirection: 'row', alignItems: 'center' }]}
            onPress={() => navigation.navigate('CompanyProfileScreen')}>
            {/* Company logo synced from CompanyProfileScreen */}
            <View style={{ marginRight: 10 }}>
              {companyLogo ? (
                <Image source={{ uri: companyLogo || undefined }} style={{ width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' }} />
              ) : (
                <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
              )}
            </View>
            <View>
              <Text style={styles.headerTitle}>Job Details</Text>
              {companyName
                ? <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 1 }}>{companyName}</Text>
                : <Text style={styles.headerSubtitle}>{jobData.title}</Text>}
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => setShowActionModal(true)}
          >
            <MaterialCommunityIcons name="dots-vertical" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </LinearGradient>
  );

  const renderJobOverview = () => (
    <View style={styles.overviewContainer}>
      {/* Job Status Card */}
      <View style={styles.statusCard}>
        <View style={styles.statusHeader}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(jobData.status || 'draft') }]}>
            <Text style={styles.statusText}>{(jobData.status || 'draft').toUpperCase()}</Text>
          </View>
          <Text style={styles.jobId}>ID: {jobData.id}</Text>
        </View>
        
        <Text style={styles.jobTitle}>{jobData.title}</Text>
        <Text style={styles.jobDepartment}>{jobData.department} • {jobData.location}</Text>
        
        <View style={styles.jobMetrics}>
          <View style={styles.metric}>
            <MaterialCommunityIcons name="account-group" size={20} color="#4A90E2" />
            <Text style={styles.metricValue}>{jobData.applicants || 0}</Text>
            <Text style={styles.metricLabel}>Applicants</Text>
          </View>
          <View style={styles.metric}>
            <MaterialCommunityIcons name="eye" size={20} color="#10B981" />
            <Text style={styles.metricValue}>{jobData.views || 0}</Text>
            <Text style={styles.metricLabel}>Views</Text>
          </View>
          <View style={styles.metric}>
            <MaterialCommunityIcons name="calendar" size={20} color="#F59E0B" />
            <Text style={styles.metricValue}>{getDaysRemaining(jobData.applicationDeadline)}</Text>
            <Text style={styles.metricLabel}>Days Left</Text>
          </View>
        </View>
      </View>

      {/* Job Details */}
      <View style={styles.detailsCard}>
        <Text style={styles.cardTitle}>Job Information</Text>
        
        <View style={styles.detailRow}>
          <MaterialCommunityIcons name="briefcase" size={20} color="#6B7280" />
          <View style={styles.detailContent}>
            <Text style={styles.detailLabel}>Employment Type</Text>
            <Text style={styles.detailValue}>{jobData.employmentType?.replace('-', ' ').toUpperCase() || 'Full-time'}</Text>
          </View>
        </View>

        <View style={styles.detailRow}>
          <MaterialCommunityIcons name="currency-gbp" size={20} color="#6B7280" />
          <View style={styles.detailContent}>
            <Text style={styles.detailLabel}>Salary Range</Text>
            <Text style={styles.detailValue}>
              {formatCurrency(jobData.salaryMin)} - {formatCurrency(jobData.salaryMax)}
            </Text>
          </View>
        </View>

        <View style={styles.detailRow}>
          <MaterialCommunityIcons name="calendar-plus" size={20} color="#6B7280" />
          <View style={styles.detailContent}>
            <Text style={styles.detailLabel}>Posted Date</Text>
            <Text style={styles.detailValue}>{formatDate(jobData.createdAt)}</Text>
          </View>
        </View>

        <View style={styles.detailRow}>
          <MaterialCommunityIcons name="calendar-clock" size={20} color="#6B7280" />
          <View style={styles.detailContent}>
            <Text style={styles.detailLabel}>Application Deadline</Text>
            <Text style={styles.detailValue}>{formatDate(jobData.applicationDeadline)}</Text>
          </View>
        </View>

        {jobData.isRemote && (
          <View style={styles.detailRow}>
            <MaterialCommunityIcons name="home" size={20} color="#10B981" />
            <View style={styles.detailContent}>
              <Text style={styles.detailLabel}>Remote Work</Text>
              <Text style={[styles.detailValue, { color: '#10B981' }]}>Available</Text>
            </View>
          </View>
        )}
      </View>

      {/* Job Description */}
      <View style={styles.descriptionCard}>
        <Text style={styles.cardTitle}>Job Description</Text>
        <Text style={styles.descriptionText}>
          {jobData.description || 'No description available.'}
        </Text>
      </View>

      {/* Requirements */}
      <View style={styles.requirementsCard}>
        <Text style={styles.cardTitle}>Requirements</Text>
        <Text style={styles.requirementsText}>
          {jobData.requirements || 'No specific requirements listed.'}
        </Text>
      </View>

      {/* Responsibilities */}
      <View style={styles.responsibilitiesCard}>
        <Text style={styles.cardTitle}>Responsibilities</Text>
        <Text style={styles.responsibilitiesText}>
          {jobData.responsibilities || 'No specific responsibilities listed.'}
        </Text>
      </View>

      {/* Notes Section */}
      <View style={styles.notesCard}>
        <View style={styles.notesHeader}>
          <Text style={styles.cardTitle}>Notes</Text>
          <TouchableOpacity
            style={styles.editNotesButton}
            onPress={() => setShowNotesModal(true)}
          >
            <MaterialCommunityIcons name="pencil" size={16} color="#4A90E2" />
            <Text style={styles.editNotesText}>Edit</Text>
          </TouchableOpacity>
        </View>
        <Text style={styles.notesText}>
          {notes || 'No notes added yet. Tap Edit to add notes about this job posting.'}
        </Text>
      </View>
    </View>
  );

  const renderApplicants = () => (
    <View style={styles.applicantsContainer}>
      <View style={styles.applicantsHeader}>
        <Text style={styles.applicantsTitle}>Applicants ({applicants.length})</Text>
        <TouchableOpacity
          style={styles.viewAllButton}
          onPress={() => navigation.navigate('ApplicantsPipelineScreen', { 
            jobId: jobData.id,
            jobTitle: jobData.title,
            applicantCount: applicants.length,
            companyLogo, companyName,
            filterByJob: true,
            jobData: { id: jobData.id, job_id: jobData.job_id || jobData.id, title: jobData.title, department: jobData.department, type: jobData.employmentType || jobData.type, location: jobData.location, salaryMin: jobData.salaryMin, salaryMax: jobData.salaryMax, status: jobData.status || "draft" },
          })}
        >
          <Text style={styles.viewAllText}>View All</Text>
          <MaterialCommunityIcons name="arrow-right" size={16} color="#4A90E2" />
        </TouchableOpacity>
      </View>

      {applicants.slice(0, 5).map((applicant) => (
          <TouchableOpacity
            key={applicant.id}
            style={styles.applicantCard}
            onPress={() => navigation.navigate('ApplicantDetailsScreen', { applicant, jobId: jobData.id })}
          >
            <View style={styles.applicantHeader}>
              <View style={styles.applicantInfo}>
                <Text style={styles.applicantName}>{applicant.name}</Text>
                <Text style={styles.applicantEmail}>{applicant.email}</Text>
              </View>
              <View style={[styles.applicantStatus, { backgroundColor: getApplicantStatusColor(applicant.status || 'new') }]}>
                <Text style={styles.applicantStatusText}>{(applicant.status || 'new').toUpperCase()}</Text>
              </View>
            </View>
            
            <View style={styles.applicantDetails}>
              <View style={styles.applicantMeta}>
                <MaterialCommunityIcons name="briefcase" size={14} color="#6B7280" />
                <Text style={styles.applicantMetaText}>{applicant.experience}</Text>
              </View>
              <View style={styles.applicantMeta}>
                <MaterialCommunityIcons name="calendar" size={14} color="#6B7280" />
                <Text style={styles.applicantMetaText}>{formatDate(applicant.appliedAt)}</Text>
              </View>
              <View style={styles.applicantMeta}>
                <MaterialCommunityIcons name="source-branch" size={14} color="#6B7280" />
                <Text style={styles.applicantMetaText}>{applicant.source}</Text>
              </View>
            </View>

            <View style={styles.applicantSkills}>
              {(applicant.skills || []).slice(0, 3).map((skill, index) => (
                <View key={index} style={styles.skillTag}>
                  <Text style={styles.skillText}>{skill}</Text>
                </View>
              ))}
              {(applicant.skills || []).length > 3 && (
                <Text style={styles.moreSkills}>+{applicant.skills.length - 3} more</Text>
              )}
            </View>
          </TouchableOpacity>
        ))}
    </View>
  );

  const renderAnalytics = () => (
    <View style={styles.analyticsContainer}>
      <Text style={styles.analyticsTitle}>Job Performance</Text>
      
      {/* Key Metrics */}
      <View style={styles.metricsGrid}>
        <View style={styles.metricCard}>
          <MaterialCommunityIcons name="eye" size={24} color="#3B82F6" />
          <Text style={styles.metricCardValue}>{analytics.views}</Text>
          <Text style={styles.metricCardLabel}>Total Views</Text>
        </View>
        <View style={styles.metricCard}>
          <MaterialCommunityIcons name="account-plus" size={24} color="#10B981" />
          <Text style={styles.metricCardValue}>{analytics.applications}</Text>
          <Text style={styles.metricCardLabel}>Applications</Text>
        </View>
        <View style={styles.metricCard}>
          <MaterialCommunityIcons name="percent" size={24} color="#F59E0B" />
          <Text style={styles.metricCardValue}>{analytics.conversionRate}%</Text>
          <Text style={styles.metricCardLabel}>Conversion</Text>
        </View>
        <View style={styles.metricCard}>
          <MaterialCommunityIcons name="clock" size={24} color="#8B5CF6" />
          <Text style={styles.metricCardValue}>{analytics.averageTimeToApply}d</Text>
          <Text style={styles.metricCardLabel}>Avg. Time</Text>
        </View>
      </View>

      {/* Traffic Sources */}
      <View style={styles.sourcesCard}>
        <Text style={styles.sourcesTitle}>Traffic Sources</Text>
        {analytics.topSources?.map((source, index) => (
          <View key={index} style={styles.sourceRow}>
            <Text style={styles.sourceName}>{source.source}</Text>
            <View style={styles.sourceProgress}>
              <View style={[styles.sourceBar, { width: `${source.percentage}%` }]} />
            </View>
            <Text style={styles.sourceCount}>{source.count}</Text>
          </View>
        ))}
      </View>

      {/* Daily Views Chart */}
      <View style={styles.chartCard}>
        <Text style={styles.chartTitle}>Daily Views (Last 7 Days)</Text>
        <View style={styles.chart}>
          {analytics.dailyViews?.map((day, index) => (
            <View key={index} style={styles.chartBar}>
              <View style={[styles.bar, { height: `${(day.views / 20) * 100}%` }]} />
              <Text style={styles.chartLabel}>{day.day}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );

  const renderTesting = () => (
    <View style={styles.settingsContainer}>
      <Text style={styles.settingsTitle}>Testing & Assessment</Text>
      <Text style={{ fontSize: 13, color: '#6B7280', marginBottom: 12, paddingHorizontal: 16 }}>
        Manage tests, view results, and track candidate assessments for {jobData.title}
      </Text>
      <View style={styles.settingsCard}>
        <TouchableOpacity style={styles.settingRow} onPress={() => navigation.navigate('TestApplicantsScreen', { jobId: jobData.id, jobTitle: jobData.title, companyLogo, companyName })}>
          <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: '#8B5CF620', alignItems: 'center', justifyContent: 'center' }}>
            <MaterialCommunityIcons name="clipboard-check" size={22} color="#8B5CF6" />
          </View>
          <View style={[styles.settingContent, { marginLeft: 12 }]}>
            <Text style={styles.settingTitle}>Test Applicants</Text>
            <Text style={styles.settingSubtitle}>Assign tests, set deadlines, invite candidates</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={() => navigation.navigate('TestCandidatesPipelineScreen', { jobId: jobData.id, jobTitle: jobData.title, companyLogo, companyName })}>
          <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: '#3B82F620', alignItems: 'center', justifyContent: 'center' }}>
            <MaterialCommunityIcons name="account-clock" size={22} color="#3B82F6" />
          </View>
          <View style={[styles.settingContent, { marginLeft: 12 }]}>
            <Text style={styles.settingTitle}>Candidates Pipeline</Text>
            <Text style={styles.settingSubtitle}>Track progress: pending, in-progress, completed</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={() => navigation.navigate('TestResultsScreen', { jobId: jobData.id, jobTitle: jobData.title, companyLogo, companyName })}>
          <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: '#10B98120', alignItems: 'center', justifyContent: 'center' }}>
            <MaterialCommunityIcons name="chart-box" size={22} color="#10B981" />
          </View>
          <View style={[styles.settingContent, { marginLeft: 12 }]}>
            <Text style={styles.settingTitle}>Test Results</Text>
            <Text style={styles.settingSubtitle}>Scores, rankings, pass/fail analytics</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={() => navigation.navigate('TestsBankManagementScreen', { jobId: jobData.id, jobTitle: jobData.title, companyLogo, companyName })}>
          <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: '#F59E0B20', alignItems: 'center', justifyContent: 'center' }}>
            <MaterialCommunityIcons name="database-cog" size={22} color="#F59E0B" />
          </View>
          <View style={[styles.settingContent, { marginLeft: 12 }]}>
            <Text style={styles.settingTitle}>Tests Bank</Text>
            <Text style={styles.settingSubtitle}>Create questions, manage test suites</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>
      </View>

      <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#8B5CF6', borderRadius: 10, padding: 14, marginHorizontal: 16, marginTop: 16 }}
        onPress={() => navigation.navigate('TestApplicantsScreen', { jobId: jobData.id, jobTitle: jobData.title, companyLogo, companyName })}>
        <MaterialCommunityIcons name="play-circle" size={20} color="#FFF" />
        <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 15, marginLeft: 8 }}>Start Testing Candidates</Text>
      </TouchableOpacity>
    </View>
  );

  const renderSettings = () => (
    <View style={styles.settingsContainer}>
      <Text style={styles.settingsTitle}>Job Settings</Text>
      
      <View style={styles.settingsCard}>
        <TouchableOpacity style={styles.settingRow} onPress={handleEditJob}>
          <MaterialCommunityIcons name="pencil" size={20} color="#4A90E2" />
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>Edit Job Details</Text>
            <Text style={styles.settingSubtitle}>Modify job information and requirements</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={handleDuplicateJob}>
          <MaterialCommunityIcons name="content-copy" size={20} color="#10B981" />
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>Duplicate Job</Text>
            <Text style={styles.settingSubtitle}>Create a copy of this job posting</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={() => setShowShareModal(true)}>
          <MaterialCommunityIcons name="share-variant" size={20} color="#8B5CF6" />
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>Share Job</Text>
            <Text style={styles.settingSubtitle}>Share this job posting externally</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={handleToggleStatus}>
          <MaterialCommunityIcons 
            name={(jobData.status || 'draft') === 'active' ? 'pause' : 'play'} 
            size={20} 
            color={(jobData.status || 'draft') === 'active' ? '#F59E0B' : '#10B981'} 
          />
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>
              <Text>{(jobData.status || 'draft') === 'active' ? 'Pause Job' : 'Activate Job'}</Text>
            </Text>
            <Text style={styles.settingSubtitle}>
              <Text>{(jobData.status || 'draft') === 'active' ? 'Temporarily pause applications' : 'Resume accepting applications'}</Text>
            </Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={handleCloseJob}>
          <MaterialCommunityIcons name="close-circle" size={20} color="#EF4444" />
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>Close Job</Text>
            <Text style={styles.settingSubtitle}>Mark this job as closed permanently</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>
      </View>

      {/* Testing & Assessment */}
      <Text style={[styles.settingsTitle, { marginTop: 16 }]}>Testing & Assessment</Text>
      <View style={styles.settingsCard}>
        <TouchableOpacity style={styles.settingRow} onPress={() => navigation.navigate('TestApplicantsScreen', { jobId: jobData.id, jobTitle: jobData.title, companyLogo, companyName })}>
          <MaterialCommunityIcons name="clipboard-check" size={20} color="#8B5CF6" />
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>Test Applicants</Text>
            <Text style={styles.settingSubtitle}>Assign and manage tests for applicants</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={() => navigation.navigate('TestCandidatesPipelineScreen', { jobId: jobData.id, jobTitle: jobData.title, companyLogo, companyName })}>
          <MaterialCommunityIcons name="account-clock" size={20} color="#3B82F6" />
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>Candidates Pipeline</Text>
            <Text style={styles.settingSubtitle}>Track candidate test progress</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={() => navigation.navigate('TestResultsScreen', { jobId: jobData.id, jobTitle: jobData.title, companyLogo, companyName })}>
          <MaterialCommunityIcons name="chart-box" size={20} color="#10B981" />
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>Test Results</Text>
            <Text style={styles.settingSubtitle}>View and analyze test scores</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>

        <TouchableOpacity style={styles.settingRow} onPress={() => navigation.navigate('TestsBankManagementScreen', { jobId: jobData.id, jobTitle: jobData.title, companyLogo, companyName })}>
          <MaterialCommunityIcons name="database-cog" size={20} color="#F59E0B" />
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>Tests Bank</Text>
            <Text style={styles.settingSubtitle}>Manage questions and test suites</Text>
          </View>
          <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderTabNavigation = () => (
    <View style={styles.tabContainer}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[styles.tab, activeTab === tab.id && styles.activeTab]}
            onPress={() => setActiveTab(tab.id)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.id ? '#4A90E2' : '#9CA3AF'} 
            />
            <Text style={[styles.tabText, activeTab === tab.id && styles.activeTabText]}>
              {tab.name}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderJobOverview();
      case 'applicants':
        return renderApplicants();
      case 'social':
        return renderSocialTab();
      case 'analytics':
        return renderAnalytics();
      case 'testing':
        return renderTesting();
      case 'settings':
        return renderSettings();
      default:
        return renderJobOverview();
    }
  };

  const renderActionModal = () => (
    <Modal
      visible={showActionModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowActionModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.actionModal}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Job Actions</Text>
            <TouchableOpacity onPress={() => setShowActionModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          
          <ScrollView style={styles.actionsList}>
            {jobActions.map((action) => (
              <TouchableOpacity
                key={action.id}
                style={styles.actionItem}
                onPress={action.action}
              >
                <View style={[styles.actionIcon, { backgroundColor: action.color }]}>
                  <MaterialCommunityIcons name={action.icon} size={20} color="#FFFFFF" />
                </View>
                <View style={styles.actionContent}>
                  <Text style={styles.actionTitle}>{action.title}</Text>
                  <Text style={styles.actionSubtitle}>{action.subtitle}</Text>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF" />
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );


  // Social Media Management Tab
  const renderSocialTab = () => (
    <ScrollView style={{ padding: 16 }}>
      {/* Share to All */}
      <TouchableOpacity style={{ backgroundColor: '#3B82F6', borderRadius: 12, padding: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}
        onPress={handleShareToAll}>
        <MaterialCommunityIcons name="share-variant-outline" size={22} color="#FFF" />
        <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 16, marginLeft: 8 }}>Share to All Platforms</Text>
      </TouchableOpacity>

      {/* Download & Generate */}
      <View style={{ flexDirection: 'row', marginBottom: 16, gap: 8 }}>
        <TouchableOpacity style={{ flex: 1, backgroundColor: '#EF4444', borderRadius: 12, padding: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}
          onPress={generateJobPDF}>
          <MaterialCommunityIcons name="file-pdf-box" size={20} color="#FFF" />
          <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 14, marginLeft: 6 }}>Download PDF</Text>
        </TouchableOpacity>
        <TouchableOpacity style={{ flex: 1, backgroundColor: '#0e4e96', borderRadius: 12, padding: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}
          onPress={generateJobCard}>
          <MaterialCommunityIcons name="card-account-details" size={20} color="#FFF" />
          <Text style={{ color: '#FFF', fontWeight: '700', fontSize: 14, marginLeft: 6 }}>Social Card</Text>
        </TouchableOpacity>
      </View>

      {/* Company Logo Preview */}
      <View style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1, borderColor: '#E5E7EB', flexDirection: 'row', alignItems: 'center' }}>
        {companyLogo
          ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 56, height: 56, borderRadius: 28, marginRight: 14, borderWidth: 2, borderColor: '#E5E7EB' }} resizeMode="cover" />
          : <View style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: '#0e4e96', marginRight: 14, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ color: '#FFF', fontSize: 18, fontWeight: '900' }}>{(companyName || 'CO').split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase()}</Text>
            </View>}
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 16, fontWeight: '700', color: '#1F2937' }}>{companyName || 'Your Company'}</Text>
          <Text style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Logo used in PDF &amp; social posts</Text>
          <TouchableOpacity onPress={() => navigation?.navigate('CompanyProfileScreen')}>
            <Text style={{ fontSize: 12, color: '#3B82F6', fontWeight: '600', marginTop: 4 }}>Update logo →</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Social Media Platforms */}
      <Text style={{ fontSize: 17, fontWeight: '700', color: '#1F2937', marginBottom: 12 }}>Post to Social Media</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {shareOptions.map((option) => (
          <TouchableOpacity key={option.id} style={{ width: Dimensions.get('window').width / 2 - 32, backgroundColor: '#FFF', borderRadius: 12, padding: 14, margin: 4, borderWidth: 1, borderColor: '#E5E7EB', alignItems: 'center' }}
            onPress={() => { option.action(); trackShare(option.id); }}>
            <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: option.color + '15', justifyContent: 'center', alignItems: 'center', marginBottom: 8 }}>
              <MaterialCommunityIcons name={option.icon} size={22} color={option.color} />
            </View>
            <Text style={{ fontSize: 13, fontWeight: '600', color: '#1F2937', textAlign: 'center' }}>{option.title}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Job Post Preview */}
      <Text style={{ fontSize: 17, fontWeight: '700', color: '#1F2937', marginTop: 20, marginBottom: 12 }}>Post Preview</Text>
      <View style={{ backgroundColor: '#FFF', borderRadius: 12, padding: 16, borderWidth: 1, borderColor: '#E5E7EB' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' }}>
          {companyLogo
            ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 48, height: 48, borderRadius: 24, marginRight: 12, borderWidth: 2, borderColor: '#E5E7EB' }} resizeMode="cover" />
            : <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: '#0e4e96', marginRight: 12, alignItems: 'center', justifyContent: 'center' }}>
                <Text style={{ color: '#FFF', fontSize: 16, fontWeight: '900' }}>{(companyName || 'CO').split(' ').slice(0,2).map(w => w[0]).join('').toUpperCase()}</Text>
              </View>}
          <View>
            <Text style={{ fontSize: 15, fontWeight: '700', color: '#1F2937' }}>{companyName || 'Your Company'}</Text>
            <Text style={{ fontSize: 12, color: '#6B7280' }}>{jobData.department || ''} {jobData.location ? '· ' + jobData.location : ''}</Text>
          </View>
        </View>
        <Text style={{ fontSize: 16, fontWeight: '700', color: '#1F2937' }}>🚀 We're Hiring: {jobData.title}</Text>
        <Text style={{ fontSize: 14, color: '#6B7280', marginTop: 8, lineHeight: 20 }}>
          🏢 {companyName}{"\n"}
          📍 {jobData.location || 'Remote'}{"\n"}
          💼 {jobData.department || ''}{"\n"}
          💰 {formatCurrency(jobData.salaryMin)} - {formatCurrency(jobData.salaryMax)}{"\n"}
          ⏰ {jobData.type || 'Full-time'}
        </Text>
        <Text style={{ fontSize: 13, color: '#6B7280', marginTop: 8 }} numberOfLines={4}>{jobData.description}</Text>
        <Text style={{ fontSize: 13, color: '#3B82F6', fontWeight: '600', marginTop: 8 }}>✅ Apply now: https://jobeng.app/jobs/{jobData.id}</Text>
      </View>

      {/* Job Board Posting */}
      <Text style={{ fontSize: 17, fontWeight: '700', color: '#1F2937', marginTop: 20, marginBottom: 12 }}>Post to Job Boards</Text>
      {[
        { name: 'Indeed', icon: 'briefcase-search', color: '#2164F3', url: 'https://employers.indeed.com' },
        { name: 'Glassdoor', icon: 'star-circle', color: '#0CAA41', url: 'https://www.glassdoor.com/employer' },
        { name: 'Reed.co.uk', icon: 'web', color: '#D4002A', url: 'https://www.reed.co.uk/recruiter' },
        { name: 'Totaljobs', icon: 'web', color: '#3F1D75', url: 'https://www.totaljobs.com/recruiter' },
        { name: 'CV-Library', icon: 'file-account', color: '#00A4E0', url: 'https://www.cv-library.co.uk/recruiter' },
        { name: 'Monster', icon: 'web', color: '#6E45A5', url: 'https://hiring.monster.co.uk' },
      ].map((board, i) => (
        <TouchableOpacity key={i} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF', borderRadius: 10, padding: 14, marginBottom: 8, borderWidth: 1, borderColor: '#E5E7EB' }}
          onPress={() => { Linking.openURL(board.url).catch(() => {}); trackShare(String(board.name ?? '').toLowerCase()); }}>
          <View style={{ width: 40, height: 40, borderRadius: 20, backgroundColor: board.color + '15', justifyContent: 'center', alignItems: 'center', marginRight: 12 }}>
            <MaterialCommunityIcons name={board.icon} size={20} color={board.color} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontSize: 15, fontWeight: '600', color: '#1F2937' }}>{board.name}</Text>
            <Text style={{ fontSize: 12, color: '#6B7280' }}>Post job to {board.name}</Text>
          </View>
          <MaterialCommunityIcons name="open-in-new" size={18} color="#9CA3AF" />
        </TouchableOpacity>
      ))}

      {/* Share Statistics */}
      <Text style={{ fontSize: 17, fontWeight: '700', color: '#1F2937', marginTop: 20, marginBottom: 12 }}>Share Performance</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Shares', value: analytics?.totalShares || 0, icon: 'share-variant', color: '#3B82F6' },
          { label: 'Link Clicks', value: analytics?.linkClicks || 0, icon: 'cursor-default-click', color: '#10B981' },
          { label: 'Applications', value: analytics?.applicationsFromShare || 0, icon: 'account-plus', color: '#F59E0B' },
          { label: 'Impressions', value: analytics?.socialImpressions || 0, icon: 'eye', color: '#8B5CF6' },
        ].map((stat, i) => (
          <View key={i} style={{ width: Dimensions.get('window').width / 2 - 32, backgroundColor: '#FFF', borderRadius: 10, padding: 14, margin: 4, borderWidth: 1, borderColor: '#E5E7EB', alignItems: 'center' }}>
            <MaterialCommunityIcons name={stat.icon} size={22} color={stat.color} />
            <Text style={{ fontSize: 20, fontWeight: 'bold', color: stat.color, marginTop: 4 }}>{stat.value}</Text>
            <Text style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{stat.label}</Text>
          </View>
        ))}
      </View>
      <View style={{ height: 40 }} />
    </ScrollView>
  );

  const renderShareModal = () => (
    <Modal
      visible={showShareModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowShareModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.shareModal}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Share Job</Text>
            <TouchableOpacity onPress={() => setShowShareModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          
          <View style={styles.shareOptions}>
            {shareOptions.map((option) => (
              <TouchableOpacity
                key={option.id}
                style={styles.shareOption}
                onPress={option.action}
              >
                <View style={[styles.shareIcon, { backgroundColor: option.color }]}>
                  <MaterialCommunityIcons name={option.icon} size={24} color="#FFFFFF" />
                </View>
                <Text style={styles.shareTitle}>{option.title}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderNotesModal = () => (
    <Modal
      visible={showNotesModal}
      transparent
      animationType="slide"
      onRequestClose={() => setShowNotesModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.notesModal}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Job Notes</Text>
            <TouchableOpacity onPress={() => setShowNotesModal(false)}>
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
          
          <TextInput
            style={styles.notesInput}
            value={notes}
            onChangeText={setNotes}
            placeholder="Add notes about this job posting..."
            multiline
            textAlignVertical="top"
          />
          
          <TouchableOpacity style={styles.saveNotesButton} onPress={saveNotes}>
            <Text style={styles.saveNotesText}>Save Notes</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#4A90E2" />
        <Text style={styles.loadingText}>Loading job details...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {renderHeader()}
      {renderTabNavigation()}
      
      <ScrollView
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {renderTabContent()}
      </ScrollView>

      {renderActionModal()}
      {renderShareModal()}
      {renderNotesModal()}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    paddingBottom: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 10,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    marginLeft: 15,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    marginRight: 10,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#4A90E2',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#9CA3AF',
    marginLeft: 8,
  },
  activeTabText: {
    color: '#4A90E2',
  },
  content: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    fontSize: 16,
    color: '#6B7280',
    marginTop: 10,
  },
  
  // Overview Styles
  overviewContainer: {
    padding: 20,
  },
  statusCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  statusHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  jobId: {
    fontSize: 12,
    color: '#9CA3AF',
    fontFamily: 'monospace',
  },
  jobTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 8,
  },
  jobDepartment: {
    fontSize: 16,
    color: '#6B7280',
    marginBottom: 20,
  },
  jobMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  metric: {
    alignItems: 'center',
  },
  metricValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
    marginTop: 5,
  },
  metricLabel: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 2,
  },
  detailsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 15,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  detailContent: {
    marginLeft: 15,
    flex: 1,
  },
  detailLabel: {
    fontSize: 14,
    color: '#9CA3AF',
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1F2937',
  },
  descriptionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  descriptionText: {
    fontSize: 16,
    lineHeight: 24,
    color: '#4B5563',
  },
  requirementsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  requirementsText: {
    fontSize: 16,
    lineHeight: 24,
    color: '#4B5563',
  },
  responsibilitiesCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  responsibilitiesText: {
    fontSize: 16,
    lineHeight: 24,
    color: '#4B5563',
  },
  notesCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  notesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  editNotesButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  editNotesText: {
    fontSize: 14,
    color: '#4A90E2',
    marginLeft: 5,
  },
  notesText: {
    fontSize: 16,
    lineHeight: 24,
    color: '#6B7280',
    fontStyle: 'italic',
  },

  // Applicants Styles
  applicantsContainer: {
    padding: 20,
  },
  applicantsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  applicantsTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1F2937',
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewAllText: {
    fontSize: 14,
    color: '#4A90E2',
    marginRight: 5,
  },
  applicantCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 15,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  applicantHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  applicantInfo: {
    flex: 1,
  },
  applicantName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  applicantEmail: {
    fontSize: 14,
    color: '#6B7280',
  },
  applicantStatus: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  applicantStatusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  applicantDetails: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  applicantMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 15,
  },
  applicantMetaText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginLeft: 4,
  },
  applicantSkills: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  skillTag: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  skillText: {
    fontSize: 12,
    color: '#4B5563',
  },
  moreSkills: {
    fontSize: 12,
    color: '#9CA3AF',
    fontStyle: 'italic',
  },

  // Analytics Styles
  analyticsContainer: {
    padding: 20,
  },
  analyticsTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 20,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  metricCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 15,
    width: '48%',
    alignItems: 'center',
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  metricCardValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
    marginTop: 10,
  },
  metricCardLabel: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 5,
  },
  sourcesCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sourcesTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 15,
  },
  sourceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sourceName: {
    fontSize: 14,
    color: '#4B5563',
    width: 80,
  },
  sourceProgress: {
    flex: 1,
    height: 8,
    backgroundColor: '#F3F4F6',
    borderRadius: 4,
    marginHorizontal: 10,
  },
  sourceBar: {
    height: '100%',
    backgroundColor: '#4A90E2',
    borderRadius: 4,
  },
  sourceCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    width: 30,
    textAlign: 'right',
  },
  chartCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 15,
  },
  chart: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: 120,
  },
  chartBar: {
    alignItems: 'center',
    flex: 1,
  },
  bar: {
    backgroundColor: '#4A90E2',
    width: 20,
    borderRadius: 2,
    marginBottom: 8,
  },
  chartLabel: {
    fontSize: 12,
    color: '#9CA3AF',
  },

  // Settings Styles
  settingsContainer: {
    padding: 20,
  },
  settingsTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 20,
  },
  settingsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  settingContent: {
    flex: 1,
    marginLeft: 15,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1F2937',
    marginBottom: 2,
  },
  settingSubtitle: {
    fontSize: 14,
    color: '#9CA3AF',
  },

  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  actionModal: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.7,
  },
  shareModal: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 20,
  },
  notesModal: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: 20,
    maxHeight: height * 0.8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
  },
  actionsList: {
    maxHeight: height * 0.5,
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  actionIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionContent: {
    flex: 1,
    marginLeft: 15,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1F2937',
    marginBottom: 2,
  },
  actionSubtitle: {
    fontSize: 14,
    color: '#9CA3AF',
  },
  shareOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 20,
    justifyContent: 'space-around',
  },
  shareOption: {
    alignItems: 'center',
    width: '22%',
    marginBottom: 20,
  },
  shareIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  shareTitle: {
    fontSize: 12,
    color: '#4B5563',
    textAlign: 'center',
  },
  notesInput: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 15,
    margin: 20,
    height: 150,
    fontSize: 16,
    color: '#1F2937',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  saveNotesButton: {
    backgroundColor: '#4A90E2',
    borderRadius: 8,
    padding: 15,
    margin: 20,
    alignItems: 'center',
  },
  saveNotesText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});